import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { PROJECTS } from "../lib/data";
import { Reveal } from "../components/Reveal";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = PROJECTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="case-study-not-found">
        <p className="font-display font-bold text-3xl text-ink">Case study not found.</p>
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-brand font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back home
        </button>
      </div>
    );
  }

  const { snapshot } = project;

  return (
    <main className="pt-32 pb-0" data-testid="case-study-page">
      <Helmet>
        <title>{`${project.title} — Case Study | Ricky Sokhi`}</title>
        <link rel="canonical" href={`${typeof window !== "undefined" ? window.location.origin : ""}/case-study/${project.slug}`} />
      </Helmet>
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm font-semibold text-ink-soft hover:text-brand transition-colors mb-12" data-testid="case-study-back">
          <ArrowLeft className="w-4 h-4" /> All case studies
        </button>

        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-6">{project.clientType}</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] max-w-3xl">
            {project.title}
          </h1>
        </Reveal>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="mt-12 glass rounded-3xl p-3">
          <img src={project.image} alt={project.title} className="w-full rounded-2xl object-cover object-top max-h-[34rem]" />
        </motion.div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 mt-16">
        <Reveal>
          <div className="glass rounded-3xl p-8 lg:p-12" data-testid="case-study-snapshot">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand mb-8">Snapshot</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
              {[["Client", snapshot.client], ["Sector", snapshot.sector], ["Duration", snapshot.duration], ["Role", snapshot.role]].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-ink-soft text-[11px] font-semibold uppercase tracking-wider mb-2">{k}</dt>
                  <dd className="text-ink font-medium leading-snug">{v}</dd>
                </div>
              ))}
            </div>
            <div className="border-t border-line pt-8">
              <p className="text-ink-soft text-[11px] font-semibold uppercase tracking-wider mb-4">Key results</p>
              <ul className="grid sm:grid-cols-2 gap-3">
                {snapshot.results.map((r) => (
                  <li key={r} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full btn-gradient flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-white" /></span>
                    <span className="text-ink font-light">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 mt-20 space-y-20">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-8" data-testid="case-study-challenge">
            <h2 className="lg:col-span-4 font-display font-bold text-3xl text-ink">The challenge</h2>
            <p className="lg:col-span-8 text-lg text-ink-soft leading-relaxed font-light">{project.challenge}</p>
          </div>
        </Reveal>

        <Reveal>
          <div className="grid lg:grid-cols-12 gap-8" data-testid="case-study-approach">
            <h2 className="lg:col-span-4 font-display font-bold text-3xl text-ink">The approach</h2>
            <ol className="lg:col-span-8 space-y-5">
              {project.approach.map((a, i) => (
                <li key={a} className="flex gap-5">
                  <span className="font-display font-extrabold text-xl text-gradient">0{i + 1}</span>
                  <span className="text-lg text-ink-soft leading-relaxed font-light pt-0.5">{a}</span>
                </li>
              ))}
            </ol>
          </div>
        </Reveal>

        <Reveal>
          <div className="grid lg:grid-cols-12 gap-8" data-testid="case-study-results">
            <h2 className="lg:col-span-4 font-display font-bold text-3xl text-ink">The results</h2>
            <ul className="lg:col-span-8 space-y-4">
              {project.results.map((r) => (
                <li key={r} className="flex items-start gap-3 text-lg text-ink-soft font-light">
                  <span className="mt-1 w-6 h-6 rounded-full btn-gradient flex items-center justify-center flex-shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 mt-24">
        <Reveal>
          <div className="glass rounded-3xl p-10 lg:p-16 text-center" data-testid="case-study-cta">
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-ink">
              Interested in <span className="text-gradient">similar results?</span> Let&rsquo;s talk.
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => navigate("/#contact")} className="inline-flex items-center gap-2 rounded-full btn-gradient px-8 py-4 text-base font-semibold shadow-xl shadow-brand/25 hover:opacity-90 transition-opacity" data-testid="case-study-book-call">
                Hire me — start a project <ArrowUpRight className="w-5 h-5" />
              </button>
              {project.live && (
                <a href={project.live} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full glass px-8 py-4 text-base font-semibold text-ink hover:text-brand transition-colors" data-testid="case-study-live">
                  Visit live site <ArrowUpRight className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
